/**
 * Japanese (日本語) — shared/top-level component strings. Product names
 * (MeshGen, SimOps, Diagnosis) stay untranslated (brand).
 */
export default {
  transcriptLink: {
    view: 'AIの記録を表示',
  },
  pipeline: {
    ariaLabel: '製品パイプライン',
    diagnosisHint: 'メッシュ可能になるまでCADを検査・修復します。',
    meshgenHint: 'クリーンな形状をメッシュ化します。',
    simopsHint: 'メッシュをシミュレーション — 単一ケースまたはスタディ全体。',
    evaltoolHint:
      '結果を評価し設計を比較 — 完了したスタディ内の評価ワークベンチ。',
  },
  common: {
    beta: 'ベータ',
    unknownError: '不明なエラー',
  },
  entitlement: {
    noSeat:
      'このワークスペースでは、{{product}} のシートがあなたに割り当てられていません。ワークスペースの管理者にシートの割り当てを依頼するか、サポートにお問い合わせください。',
    noSubscription:
      'このワークスペースには {{product}} の有効なサブスクリプションがありません。ワークスペースの管理者にサブスクリプションの登録を依頼するか、サポートにお問い合わせください。',
    thisProduct: 'この製品',
  },
  apiRefusal: {
    artifactTooLarge:
      'この実行の結果は大きすぎるため、ここでは読み込めません。実行結果をダウンロードして ParaView で開いてください。',
  },
  fileUpload: {
    tooLarge: 'ファイルが大きすぎます（{{size}}）。上限は 1 GB です。',
    invalidType:
      'ファイル形式が無効です。STEP、STP、OBJ、STL ファイルのみ対応しています。',
    rejected: 'ファイルが拒否されました。',
    uploading: '{{name}} をアップロード中…',
    uploadingPercent: 'アップロード中 {{percent}}%',
    uploadingNoName: 'アップロード中…',
    openCadFile: 'CAD ファイルを開く…',
    dragAndDrop: 'ここにファイルをドラッグ＆ドロップ',
    limit: 'ファイルあたり 1GB まで • STEP、STP、OBJ、STL',
    browseFiles: 'ファイルを選択',
  },
  floatingProgress: {
    processing: '処理中',
    liveComputing: 'ライブ計算',
  },
  meshTimer: {
    running: '実行中',
    complete: '完了',
    error: 'エラー',
    ready: '準備完了',
  },
  processStatus: {
    noActiveProject: 'アクティブなプロジェクトがありません',
    status: 'ステータス',
    fileUploaded: 'ファイルをアップロード',
    generatingMesh: 'メッシュを生成中',
    meshComplete: 'メッシュ完了',
    strategy: '戦略',
  },
  promoCode: {
    havePromoCode: 'プロモコードをお持ちですか？',
    enterCode: 'コードを入力',
    redeem: '適用',
  },
  qualityHistogram: {
    distribution: '品質分布',
    noData: '品質データがありません',
    clickPrefix: 'クリック',
    generateMesh: 'メッシュを生成',
    clickSuffix: 'してメッシュを作成し、品質指標を表示します。',
    excellent: '非常に良い',
    good: '良い',
    fair: '普通',
    poor: '悪い',
    bad: '非常に悪い',
    badToGood: '悪い → 良い',
    estimated: '～推定値',
    realData: '実データ',
    min: '最小',
    avg: '平均',
    max: '最大',
    elements: '{{count}} 要素',
    scaleTitle: 'SICN 品質スケール：',
    scaleBad: '0.0-0.2：非常に悪い',
    scalePoor: '0.2-0.4：悪い',
    scaleFair: '0.4-0.6：普通',
    scaleGood: '0.6-0.8：良い',
    scaleGreat: '0.8-1.0：非常に良い',
  },
  qualityReport: {
    title: '品質レポート',
    pass: '合格',
    flag: '警告',
    elements: '要素',
    poor: '悪い（<0.1）',
    sicnMin: 'SICN（最小）',
    gammaMin: 'Gamma（最小）',
    maxSkewness: '最大スキュー',
    maxAspectRatio: '最大アスペクト比',
    cfdQuality: 'CFD 品質',
    ready: '準備完了',
    issues: '問題あり',
    nonOrthogonality: '非直交性',
    cfdSkewness: 'CFD スキュー',
    facePyramids: '面ピラミッド',
    ok: 'OK',
    facePyramidsFlag: '警告',
    cfdReadyMesh: 'CFD 対応メッシュ（非直交 ≤70°）',
    meetsStandards: 'メッシュ品質はすべての基準を満たしています',
  },
  shareLink: {
    copyPublicLink: '公開リンクをコピー',
  },
  visibilityBadge: {
    makePrivate: 'クリックして非公開にする',
    makePublic: 'クリックして公開にする',
    public: '公開',
    private: '非公開',
  },
  shareDialog: {
    shareButton: '共有',
    title: '共有',
    description: '閲覧・コメント・編集できるユーザーを管理します。',
    peopleWithAccess: 'アクセスできるユーザー',
    roleOwner: 'オーナー',
    roleViewer: '閲覧者',
    roleCommenter: 'コメント可',
    roleEditor: '編集者',
    pendingInvite: '（招待中）',
    removeAccess: '{{email}} のアクセス権を削除',
    invitePlaceholder: 'メールアドレスでユーザーを追加',
    invite: '招待',
    invalidEmail: '有効なメールアドレスを入力してください。',
    toastInvited: '{{email}} を招待しました。',
    toastInvitedEmailFailed:
      '{{email}} を追加しましたが、招待メールを送信できませんでした。',
    generalAccess: '全般的なアクセス',
    scopeRestricted: '制限付き',
    scopeOrganization: '組織内のユーザー',
    scopeAnyoneWithLink: 'リンクを知っている全員',
    scopeRestrictedHint: 'アクセス権を持つユーザーのみが開けます。',
    scopeOrganizationHint: '組織内の誰でも開けます。',
    scopeAnyoneWithLinkHint: 'リンクを知っている人なら誰でも開けます。',
    copyLink: 'リンクをコピー',
  },
  dataTable: {
    noResults: '結果がありません。',
  },
  geometryNotFound: {
    title: 'ジオメトリが見つかりません',
    goToDashboard: 'ダッシュボードへ移動',
    loadFailed: 'このジオメトリの読み込み中に問題が発生しました。',
  },
  staleWarning: {
    messageProject:
      'このプロジェクトは最後に操作してから1週間以上経過しているため、一部の新しい機能が正しく動作しない場合があります。',
    messageStudy:
      'このスタディは最後に操作してから1週間以上経過しているため、一部の新しい機能が正しく動作しない場合があります。',
    messageGeometry:
      'このジオメトリは最後に操作してから1週間以上経過しているため、一部の新しい機能が正しく動作しない場合があります。',
    actionReupload: '再アップロード',
    actionCreateStudy: '新しいスタディを作成',
    actionRerunDiagnosis: 'Diagnosis を再実行',
    dismiss: '警告を閉じる',
  },
  toast: {
    downloadSimulationFailed:
      'シミュレーション出力のダウンロードに失敗しました',
    downloadInputCaseFailed: '入力ケースのダウンロードに失敗しました',
    visibilityUpdateFailed: '公開設定の更新に失敗しました',
    sharingUpdateFailed: '共有設定の更新に失敗しました',
  },
  readOnlyBanner: {
    viewerAccess: '閲覧者としてアクセスしています — 所有者：{{owner}}',
    commenterAccess: 'コメント可としてアクセスしています — 所有者：{{owner}}',
    viewerAccessNoOwner: '閲覧者としてアクセスしています',
    commenterAccessNoOwner: 'コメント可としてアクセスしています',
    anonymousAccess: '閲覧専用でアクセスしています',
    openInApp: 'アプリで開く',
    signInToOpenInApp: 'サインイン',
    preferences: '表示設定',
    signInPrompt: 'アクセス権が必要です',
    signInBody: 'アクセス権のあるアカウントでサインインしてください。',
    signInCta: 'サインイン',
  },
} as const;
